import { useEffect } from 'react'
import { animated, config, useSpring } from '@react-spring/web'
import PropTypes from 'prop-types'

import Box from '@mui/material/Box'

BackgroundCircles.propTypes = {
  /** Multiplier on the circle sizes, for compact hosts */
  scale: PropTypes.number,
}

export default function BackgroundCircles({ scale = 1 }) {
  const [circle1, circle1Api] = useSpring(() => ({
    from: { x: 0, y: 0, rotate: 0, scale: 1 },
    config: config.slow,
  }))

  const [circle2, circle2Api] = useSpring(() => ({
    from: { x: 0, y: 0, rotate: 0, scale: 1 },
    config: config.slow,
  }))

  const [circle3, circle3Api] = useSpring(() => ({
    from: { x: 0, y: 0, rotate: 0, scale: 1 },
    config: config.slow,
  }))

  const [circle4, circle4Api] = useSpring(() => ({
    from: { x: 0, y: 0, rotate: 0, scale: 1 },
    config: config.slow,
  }))

  useEffect(() => {
    const drift = (api, scale, duration) =>
      api.start({
        to: async (next) => {
          while (true) {
            await next({ x: 8, y: 20, rotate: 8, scale })
            await next({ x: 0, y: 0, rotate: 0, scale: 1 })
          }
        },
        config: { duration },
      })

    const apis = [circle1Api, circle2Api, circle3Api, circle4Api]
    drift(circle1Api, 1.1, 4000)
    const timeouts = [
      setTimeout(() => drift(circle2Api, 1.15, 4200), 500),
      setTimeout(() => drift(circle3Api, 1.2, 3800), 1000),
      setTimeout(() => drift(circle4Api, 1.25, 4300), 1500),
    ]

    return () => {
      timeouts.forEach(clearTimeout)
      apis.forEach((api) => api.stop())
    }
  }, [circle1Api, circle2Api, circle3Api, circle4Api])

  return (
    <Box sx={styles.container} style={{ '--circles-scale': scale }} aria-hidden="true">
      <Box
        component={animated.div}
        sx={[styles.circle, styles.circle1]}
        style={{
          transform: circle1.x.to(
            (x) => `translate(${x}px, ${circle1.y.get()}px) rotate(${circle1.rotate.get()}deg) scale(${circle1.scale.get()})`
          ),
        }}
      />

      <Box
        component={animated.div}
        sx={[styles.circle, styles.circle2]}
        style={{
          transform: circle2.x.to(
            (x) => `translate(${x}px, ${circle2.y.get()}px) rotate(${circle2.rotate.get()}deg) scale(${circle2.scale.get()})`
          ),
        }}
      />

      <Box
        component={animated.div}
        sx={[styles.circle, styles.circle3]}
        style={{
          transform: circle3.x.to(
            (x) => `translate(${x}px, ${circle3.y.get()}px) rotate(${circle3.rotate.get()}deg) scale(${circle3.scale.get()})`
          ),
        }}
      />

      <Box
        component={animated.div}
        sx={[styles.circle, styles.circle4]}
        style={{
          transform: circle4.x.to(
            (x) => `translate(${x}px, ${circle4.y.get()}px) rotate(${circle4.rotate.get()}deg) scale(${circle4.scale.get()})`
          ),
        }}
      />
    </Box>
  )
}

const styles = {
  container: {
    position: 'absolute',
    inset: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
  },
  circle: {
    position: 'absolute',
    borderRadius: '50%',
    opacity: 0.1,
  },
  circle1: {
    backgroundColor: 'brand.grey',
    width: 'calc(120px * var(--circles-scale))',
    height: 'calc(120px * var(--circles-scale))',
    top: '10%',
    right: '8%',
  },
  circle2: {
    backgroundColor: 'brand.lime',
    width: 'calc(90px * var(--circles-scale))',
    height: 'calc(90px * var(--circles-scale))',
    bottom: '20%',
    left: '10%',
  },
  circle3: {
    backgroundColor: 'brand.magenta',
    width: 'calc(75px * var(--circles-scale))',
    height: 'calc(75px * var(--circles-scale))',
    top: '50%',
    right: '20%',
  },
  circle4: {
    backgroundColor: 'brand.blue',
    width: 'calc(65px * var(--circles-scale))',
    height: 'calc(65px * var(--circles-scale))',
    top: '25%',
    left: '12%',
  },
}
